import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Typography, theme, Badge, Tooltip, Space } from 'antd';
import {
  DashboardOutlined, TeamOutlined, CompassOutlined,
  DollarOutlined, UserOutlined, ShopOutlined, FileOutlined,
  CheckSquareOutlined, BulbOutlined, BarChartOutlined, WalletOutlined,
  SettingOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  LogoutOutlined, BellOutlined, ArrowLeftOutlined, AccountBookOutlined,
  BookOutlined, FundOutlined, CreditCardOutlined, BankOutlined, ShoppingCartOutlined,
  SolutionOutlined, SafetyOutlined, UploadOutlined, FileProtectOutlined,
} from '@ant-design/icons';
import { useAuth } from '../store/AuthContext';
import API from '../utils/axios';
import { useCan } from '../utils/perms';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const menuItems = [
  { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/customers', icon: <TeamOutlined />, label: 'Customers' },
  { key: '/leads', icon: <BulbOutlined />, label: 'Leads' },
  { key: '/bookings', icon: <DollarOutlined />, label: 'Sales' },
  { key: '/quotes', icon: <FileProtectOutlined />, label: 'Quotes' },
  { key: '/packages', icon: <CompassOutlined />, label: 'Packages' },
  { key: '/agents', icon: <UserOutlined />, label: 'Agents' },
  {
    key: 'users-roles', icon: <SolutionOutlined />, label: 'Users & Roles',
    children: [
      { key: '/users', icon: <TeamOutlined />, label: 'Users' },
      { key: '/roles', icon: <SafetyOutlined />, label: 'Roles' },
    ],
  },
  { key: '/vendors', icon: <ShopOutlined />, label: 'Vendors' },
  {
    key: 'purchases', icon: <ShoppingCartOutlined />, label: 'Purchases',
    children: [
      { key: '/purchases', icon: <ShoppingCartOutlined />, label: 'Purchases' },
      { key: '/purchases/import', icon: <UploadOutlined />, label: 'Purchase Import' },
    ],
  },
  {
    key: 'accounting', icon: <AccountBookOutlined />, label: 'Accounting',
    children: [
      { key: '/accounts', icon: <BookOutlined />, label: 'Chart of Accounts' },
      { key: '/accounting/ledger', icon: <FundOutlined />, label: 'General Ledger' },
      { key: '/accounting/customer-ledger', icon: <TeamOutlined />, label: 'Customer Ledger' },
      { key: '/accounting/supplier-ledger', icon: <ShopOutlined />, label: 'Supplier Ledger' },
      { key: '/accounting/cash', icon: <BankOutlined />, label: 'Cash Book' },
      { key: '/accounting/reports', icon: <CreditCardOutlined />, label: 'Accounting Reports' },
    ],
  },
  { key: '/documents', icon: <FileOutlined />, label: 'Documents' },
  { key: '/tasks', icon: <CheckSquareOutlined />, label: 'Tasks' },
  { key: '/reports', icon: <BarChartOutlined />, label: 'Reports' },
  { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },
];

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(true);
  const [openKeys, setOpenKeys] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  const canDashboard = useCan('dashboard');
  const canCustomers = useCan('customers');
  const canLeads = useCan('leads');
  const canBookings = useCan('bookings');
  const canQuotes = useCan('quotes');
  const canPackages = useCan('packages');
  const canAgents = useCan('agents');
  const canUsers = useCan('users');
  const canVendors = useCan('vendors');
  const canPurchases = useCan('purchases');
  const canAccounting = useCan('accounting');
  const canDocuments = useCan('documents');
  const canTasks = useCan('tasks');
  const canReports = useCan('reports');
  const canSettings = useCan('settings');

  const itemModule = {
    '/': canDashboard,
    '/customers': canCustomers,
    '/leads': canLeads,
    '/bookings': canBookings,
    '/quotes': canQuotes,
    '/packages': canPackages,
    '/agents': canAgents,
    '/vendors': canVendors,
    '/purchases': canPurchases,
    '/documents': canDocuments,
    '/tasks': canTasks,
    '/reports': canReports,
    '/settings': canSettings,
    'accounting': canAccounting,
    'users-roles': canUsers,
    'purchases': canPurchases,
  };

  const filteredItems = menuItems
    .filter(item => item.children ? item.children.some(c => itemModule[item.key]) : itemModule[item.key])
    .map(item => item.children
      ? { ...item, children: item.children.filter(c => itemModule[item.key]) }
      : item);

  const pageHeader = (() => {
    const p = location.pathname;
    if (p === '/') return { title: 'Dashboard' };
    if (p === '/customers') return { title: 'Customers' };
    if (p === '/leads') return { title: 'Leads' };
    if (p === '/bookings') return { title: 'Sales' };
    if (p === '/quotes') return { title: 'Quotes' };
    if (p === '/packages') return { title: 'Packages' };
    if (p === '/agents') return { title: 'Agents & Team' };
    if (p === '/users') return { title: 'Users' };
    if (p === '/roles') return { title: 'Roles & Permissions' };
    if (p === '/vendors') return { title: 'Vendors' };
    if (p === '/purchases') return { title: 'Purchases' };
    if (p === '/purchases/import') return { title: 'Purchase Import' };
    if (p === '/accounts') return { title: 'Chart of Accounts' };
    if (p === '/accounting/ledger') return { title: 'General Ledger' };
    if (p === '/accounting/customer-ledger') return { title: 'Customer Ledger' };
    if (p === '/accounting/supplier-ledger') return { title: 'Supplier Ledger' };
    if (p === '/accounting/cash') return { title: 'Cash Book' };
    if (p === '/accounting/reports') return { title: 'Accounting Reports' };
    if (p === '/documents') return { title: 'Documents' };
    if (p === '/tasks') return { title: 'Tasks' };
    if (p === '/reports') return { title: 'Reports & Analytics' };
    if (p === '/settings') return { title: 'Settings' };
    if (p === '/bookings/new') return { back: '/bookings', title: 'New Invoice' };
    if (p.match(/^\/bookings\/edit\//i)) return { back: '/bookings', title: 'Edit Invoice' };
    if (p.match(/^\/bookings\/(?!new$|edit)[a-f0-9]{24}$/i)) return { back: '/bookings', title: 'Invoice' };
    if (p === '/quotes/new') return { back: '/quotes', title: 'New Quote' };
    if (p.match(/^\/quotes\/edit\//i)) return { back: '/quotes', title: 'Edit Quote' };
    if (p.match(/^\/quotes\/(?!new$|edit)[a-f0-9]{24}$/i)) return { back: '/quotes', title: 'Quote' };
    if (p.match(/^\/customers\/(?!new$)[a-f0-9]{24}$/i)) return { back: '/customers', title: 'Customer' };
    if (p.match(/^\/packages\/(?!new$)[a-f0-9]{24}$/i)) return { back: '/packages', title: 'Package' };
    return null;
  })();

  useEffect(() => {
    API.get('/dashboard/stats').then(({ data }) => {
      setOverdueCount(data.overdueTasks || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith('/accounting/') || p === '/accounts') {
      setOpenKeys(keys => (keys.includes('accounting') ? keys : [...keys, 'accounting']));
    }
    if (p === '/purchases' || p === '/purchases/import') {
      setOpenKeys(keys => (keys.includes('purchases') ? keys : [...keys, 'purchases']));
    }
    if (p === '/users' || p === '/roles') {
      setOpenKeys(keys => (keys.includes('users-roles') ? keys : [...keys, 'users-roles']));
    }
  }, [location.pathname]);

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: 'Profile' },
      { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') logout();
      else if (key === 'settings') navigate('/settings');
    },
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={200}
        collapsedWidth={84}
        style={{ background: '#fff', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ height: 48, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <CompassOutlined style={{ fontSize: 22, color: '#1677ff' }} />
          {!collapsed && <Text strong style={{ fontSize: 16, marginLeft: 8 }}>TravelPro</Text>}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            openKeys={collapsed ? undefined : openKeys}
            onOpenChange={setOpenKeys}
            items={filteredItems}
            onClick={({ key }) => navigate(key)}
            style={{ border: 'none', marginTop: 4 }}
          />
        </div>
      </Sider>
      <Layout>
        <Header style={{ padding: '0 16px', background: colorBgContainer, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0' }}>
          <Space size={4}>
            <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed(!collapsed)} />
            {pageHeader && (
              <>
                {pageHeader.back && <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(pageHeader.back)} />}
                <Text strong style={{ fontSize: 15 }}>{pageHeader.title}</Text>
              </>
            )}
          </Space>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Tooltip title={`${overdueCount} overdue task${overdueCount !== 1 ? 's' : ''}`}>
              <Badge count={overdueCount} size="small" offset={[-2, 2]}>
                <Button type="text" icon={<BellOutlined />} onClick={() => navigate('/tasks')} />
              </Badge>
            </Tooltip>
            <Dropdown menu={userMenu} placement="bottomRight">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <Avatar style={{ backgroundColor: '#1677ff' }} icon={<UserOutlined />} />
                <Text>{user?.name}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 0, background: '#f5f5f5', minHeight: 280 }}>
          <div style={{ padding: 24 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
